import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Edit, Trash2, FileText } from 'lucide-react';
import { documentApi } from '../lib/api';
import { formatDate, formatCurrency, formatFileSize } from '../utils/format';

export default function DocumentView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: () => documentApi.get(id!),
    enabled: !!id,
  });

  const doc = data?.data?.document;

  const handleDownload = async (docId: string, filename: string) => {
    try {
      const response = await documentApi.download(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = globalThis.document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      globalThis.document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download document');
    }
  };

  const handleDelete = async (docId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    try {
      await documentApi.delete(docId);
      alert('Document deleted successfully');
      navigate('/documents');
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Failed to delete document');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!doc) {
    return (
      <div className="text-center py-12">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Document not found</p>
        <Link to="/documents" className="text-primary-600 hover:underline">
          Back to Documents
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/documents" className="text-gray-600 hover:text-gray-900">
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{doc.filename}</h1>
            <p className="text-gray-600">Uploaded {formatDate(doc.upload_date)}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <button 
            onClick={() => handleDownload(doc.id, doc.filename)}
            className="btn-secondary"
          >
            <Download className="h-5 w-5" />
            Download
          </button>
          <button className="btn-secondary">
            <Edit className="h-5 w-5" />
            Edit
          </button>
          <button 
            onClick={() => handleDelete(doc.id)}
            className="btn-danger"
          >
            <Trash2 className="h-5 w-5" />
            Delete
          </button>
        </div>
      </div>

      {/* Document Processing Status */}
      {!doc.processed && (
        <div className="card bg-blue-50 border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>Processing:</strong> This document is being analyzed by AI. Refresh the page in a few moments to see extracted text, categories, and other details.
          </p>
        </div>
      )}
      
      {doc.processed && !doc.ocr_text && (
        <div className="card bg-yellow-50 border-yellow-200">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> AI processing completed, but no text was extracted. This may be a blank page or an image-only document. You can still download it using the button above.
          </p>
        </div>
      )}

      {/* Document Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Document Info Card */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Document Information
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Filename</p>
                <p className="text-base font-medium text-gray-900">
                  {doc.filename}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">File Type</p>
                <p className="text-base font-medium text-gray-900 uppercase">
                  {doc.file_type}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">File Size</p>
                <p className="text-base font-medium text-gray-900">
                  {formatFileSize(doc.file_size)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Uploaded</p>
                <p className="text-base font-medium text-gray-900">
                  {formatDate(doc.upload_date)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Storage</p>
                <p className="text-base font-medium text-gray-900 capitalize">
                  {doc.storage_bucket}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <p className="text-base font-medium text-green-600">
                  {doc.processed ? 'Processed' : 'Processing...'}
                </p>
              </div>
            </div>
          </div>

          {/* Receipt Details */}
          {doc.total_amount && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Receipt Details
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Vendor</p>
                  <p className="text-base font-medium text-gray-900">
                    {doc.vendor_name || 'Unknown'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date</p>
                  <p className="text-base font-medium text-gray-900">
                    {formatDate(doc.transaction_date)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="text-base font-medium text-gray-900">
                    {formatCurrency(doc.total_amount)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Tax</p>
                  <p className="text-base font-medium text-gray-900">
                    {formatCurrency(doc.tax_amount || 0)}
                  </p>
                </div>
                {doc.payment_method && (
                  <div>
                    <p className="text-sm text-gray-600">Payment Method</p>
                    <p className="text-base font-medium text-gray-900 capitalize">
                      {doc.payment_method}
                    </p>
                  </div>
                )}
                {doc.check_number && (
                  <div>
                    <p className="text-sm text-gray-600">Check Number</p>
                    <p className="text-base font-medium text-gray-900">
                      {doc.check_number}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* OCR Text */}
          {doc.ocr_text && (
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Extracted Text
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                  {doc.ocr_text}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* File Info */}
          <div className="card">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              File Information
            </h3>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-gray-600">Type</dt>
                <dd className="font-medium text-gray-900 uppercase">
                  {doc.file_type}
                </dd>
              </div>
              <div>
                <dt className="text-gray-600">Size</dt>
                <dd className="font-medium text-gray-900">
                  {formatFileSize(doc.file_size)}
                </dd>
              </div>
              <div>
                <dt className="text-gray-600">Storage</dt>
                <dd className="font-medium text-gray-900 capitalize">
                  {doc.storage_bucket}
                </dd>
              </div>
              <div>
                <dt className="text-gray-600">Upload Date</dt>
                <dd className="font-medium text-gray-900">
                  {formatDate(doc.upload_date)}
                </dd>
              </div>
              {doc.document_date && (
                <div>
                  <dt className="text-gray-600">Document Date</dt>
                  <dd className="font-medium text-gray-900">
                    {formatDate(doc.document_date)}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Categories */}
          {doc.categories && doc.categories.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                Categories
              </h3>
              <div className="flex flex-wrap gap-2">
                {doc.categories.map((cat: any) => (
                  <span
                    key={cat.id}
                    className="badge-primary"
                    style={{ backgroundColor: cat.color + '20', color: cat.color }}
                  >
                    {cat.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {doc.tags && doc.tags.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {doc.tags.map((tag: any) => (
                  <span
                    key={tag.id}
                    className="badge"
                    style={{ backgroundColor: tag.color + '20', color: tag.color }}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

